import { motion } from "framer-motion";
import { Bot, Sparkles, Clock, Shield } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";

export const AIAssistantSection = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: "Un Community Manager IA qui ne dort jamais.",
      subtitle: "OuraTrust analyse chaque avis et prépare une réponse personnalisée et professionnelle. Après votre validation, vous pouvez publier immédiatement.",
      badge: "Assistant IA",
      typing: "Génération en cours...",
      customerMessage: "L'accueil était vraiment décevant, j'ai attendu 30 minutes sans être pris en charge.",
      aiResponse: "Bonjour, nous vous remercions pour ce retour. Nous comprenons votre frustration concernant le temps d'attente. Notre équipe reste à votre entière disposition pour échanger et améliorer votre prochaine expérience.",
      tone: "Ton: Professionnel",
      controlLabel: "Gardez le contrôle",
      controlValue: "Validez, modifiez ou annulez chaque brouillon avant toute publication.",
    },
    en: {
      title: "Your 24/7 AI Reputation Manager.",
      subtitle: "OuraTrust analyzes every review and prepares a personalized, professional response for you. After your approval, you can publish immediately.",
      badge: "AI-Powered",
      typing: "Generating...",
      customerMessage: "The reception was really disappointing, I waited 30 minutes without being attended to.",
      aiResponse: "Hello, thank you for your feedback. We understand your frustration with the wait time. Our team remains at your disposal to discuss and improve your next experience.",
      tone: "Tone: Professional",
      controlLabel: "Stay in control",
      controlValue: "Approve, edit or cancel every draft before any publication.",
    },
  };

  const t = content[language];

  return (
    <section className="py-32 bg-gradient-to-b from-slate-50 via-white to-slate-50 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <Bot size={16} />
              {t.badge}
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-foreground leading-tight">
              {t.title}
            </h2>
            
            <p className="text-lg text-muted-foreground max-w-lg leading-relaxed">
              {t.subtitle}
            </p>


            {/* Security note */}
            <div className="flex items-start gap-3 p-4 bg-success/5 rounded-xl border border-success/20">
              <Shield className="text-success mt-0.5 flex-shrink-0" size={20} />
              <div>
                <p className="font-semibold text-foreground">{t.controlLabel}</p>
                <p className="text-sm text-muted-foreground">{t.controlValue}</p>
              </div>
            </div>
          </motion.div>

          {/* Right: Chat Bubble Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="bg-white rounded-3xl shadow-2xl shadow-slate-200/50 p-6 border border-slate-100">
              {/* Chat Header */}
              <div className="flex items-center gap-3 pb-4 border-b border-slate-100 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
                  <Bot className="text-white" size={20} />
                </div>
                <div>
                  <p className="font-semibold text-foreground">OuraTrust AI</p>
                  <p className="text-xs text-muted-foreground">{t.tone}</p>
                </div>
                {/* Scheduled indicator */}
                <div className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full bg-warning/10 text-warning text-xs font-medium">
                  <Clock size={12} className="animate-pulse" />
                  <span>~45min</span>
                </div>
              </div>

              {/* Customer Message */}
              <div className="mb-4">
                <p className="text-xs text-muted-foreground mb-2">{language === 'fr' ? 'Message client' : 'Customer message'}</p>
                <div className="bg-slate-100 rounded-2xl rounded-tl-sm p-4">
                  <p className="text-sm text-foreground">{t.customerMessage}</p>
                </div>
              </div>

              {/* AI Response with typing effect simulation */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Brouillon IA (à valider)' : 'AI Draft (needs approval)'}</p>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-warning/15 text-warning text-[10px] font-semibold border border-warning/20">
                    {language === 'fr' ? 'Validation requise' : 'Approval required'}
                  </span>
                </div>
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl rounded-tr-sm p-4 border border-primary/20">
                  <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="text-sm text-foreground leading-relaxed"
                  >
                    {t.aiResponse}
                  </motion.p>
                  
                  {/* Typing indicator animation */}
                  <motion.div
                    initial={{ opacity: 1 }}
                    whileInView={{ opacity: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.5 }}
                    className="flex items-center gap-1 mt-2"
                  >
                    <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                    <span className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.1s' }} />
                    <span className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                  </motion.div>
                </div>
              </div>

              {/* Sparkle decorations */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute -top-6 -right-6 w-12 h-12 text-primary/20"
              >
                <Sparkles size={48} />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
